import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { env } from "../config/env";
import { getNotificationDeepLink } from "../lib/notificationDeepLink";
import {
  notificationRealtime,
  shouldShowToastForNotification,
} from "../lib/notifications/notificationRealtime";
import { useJobToast, JOB_TOAST_TYPES, type JobToastVariant } from "../lib/notifications/useJobToast";
import { authStorage } from "../lib/storage/authStorage";
import { formatISTChatListTime } from "../utils/dateUtils";

const NOTIFICATIONS_BASE = `${env.apiBaseUrl.replace(/\/$/, "")}/api/notifications`;

type NotificationRow = {
  id: string;
  type?: string;
  category: string;
  title: string;
  body: string;
  read: boolean;
  created_at: string;
  metadata?: Record<string, unknown>;
};

const SKIP_TOAST_TYPES = new Set([
  "security.login",
  "security.login_other_device",
]);

type ToastItem = { id: string; title: string; body: string; variant?: JobToastVariant };

type LiveGeneration = {
  session_id: string;
  asset_type: string;
  template_name: string;
  stage: string;
  message: string;
  progress: number;
  elapsed_seconds?: number;
  remaining_seconds?: number;
  estimated_time?: string;
};

export default function NotificationBell() {
  const navigate = useNavigate();
  // Reactive token — re-runs effects when login/logout happens
  const [token, setToken] = useState(() => authStorage.getToken());

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationRow[]>([]);
  const [unread, setUnread] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [liveGen, setLiveGen] = useState<LiveGeneration | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLButtonElement>(null);

  // Keep token in sync with authStorage (login / logout in same tab)
  useEffect(() => {
    const onStorage = () => {
      const next = authStorage.getToken();
      setToken((prev) => {
        if (prev !== next) {
          notificationRealtime.forceReconnect();
        }
        return next;
      });
    };
    window.addEventListener("storage", onStorage);
    // Poll for same-tab auth changes (localStorage doesn't fire storage event in same tab)
    const poll = setInterval(() => {
      const next = authStorage.getToken();
      setToken((prev) => {
        if (prev !== next) {
          notificationRealtime.forceReconnect();
          if (!next) setLiveGen(null); // clear on logout
        }
        return next ?? null;
      });
    }, 3000);
    return () => {
      window.removeEventListener("storage", onStorage);
      clearInterval(poll);
    };
  }, []);

  const pushToast = useCallback((
    title: string,
    body: string,
    notificationId?: string,
    variant?: JobToastVariant,
    duration = 4500,
  ) => {
    if (notificationId && !shouldShowToastForNotification(notificationId)) {
      return;
    }
    const id =
      notificationId ||
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Date.now()));
    setToasts((prev) => {
      if (prev.some((t) => t.id === id)) return prev;
      return [...prev, { id, title, body, variant }];
    });
    if (duration > 0) {
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${NOTIFICATIONS_BASE}?limit=1`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setUnread(Number(data.unread_count ?? 0));
    } catch {
      /* ignore */
    }
  }, [token]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    try {
      const res = await fetch(`${NOTIFICATIONS_BASE}?limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setItems(data.notifications ?? []);
      setUnread(Number(data.unread_count ?? 0));
    } catch {
      /* ignore */
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  // Stable refs so the WS effect can read current values without reconnecting.
  const openRef = useRef(open);
  const fetchUnreadCountRef = useRef(fetchUnreadCount);
  const fetchListRef = useRef(fetchList);
  const pushToastRef = useRef(pushToast);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => { fetchUnreadCountRef.current = fetchUnreadCount; }, [fetchUnreadCount]);
  useEffect(() => { fetchListRef.current = fetchList; }, [fetchList]);
  useEffect(() => { pushToastRef.current = pushToast; }, [pushToast]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        dropdownRef.current?.contains(t) ||
        bellRef.current?.contains(t)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Job toasts — styled by variant (info / success / error), managed by useJobToast.
  useJobToast(({ id, variant, title, body, duration }) => {
    pushToastRef.current(title, body, id, variant, duration);
  });

  // Shared notification WS (one connection per tab regardless of bell mount count).
  useEffect(() => {
    if (!token) return;
    return notificationRealtime.subscribe((msg) => {
      if (msg.type === "notification:connected") {
        fetchUnreadCountRef.current();
        return;
      }

      // ── ai_media:progress — live generation card + followup badge ──────────
      if (msg.type === "ai_media:progress") {
        const m = msg as {
          type: string; stage?: string; session_id?: string; message?: string;
          template_name?: string; asset_type?: string; progress?: number;
          elapsed_seconds?: number; remaining_seconds?: number; estimated_time?: string;
          phase?: string;
        };
        const stage = m.stage ?? "";
        const sessionId = m.session_id ?? "";
        const assetType = m.asset_type ?? "flyer";
        const templateName = m.template_name ?? assetType;
        const progress = typeof m.progress === "number" ? m.progress : 0;

        if (stage === "complete") {
          // Clear live card + show success toast
          setLiveGen(null);
          pushToastRef.current(
            `${templateName} ready! ✅`,
            "Your generation is complete. Tap to view.",
            `ai_media_done:${sessionId}`,
            "success",
            5000,
          );
          return;
        }

        if (stage === "error") {
          setLiveGen(null);
          pushToastRef.current(
            `${templateName} failed`,
            m.message || "Generation failed. Please try again.",
            `ai_media_err:${sessionId}`,
            "error",
            5000,
          );
          return;
        }

        if (stage === "followup") {
          // Clear live card + add bell item
          setLiveGen(null);
          const syntheticId = `followup:${sessionId || Date.now()}`;
          const syntheticRow: NotificationRow = {
            id: syntheticId,
            type: "ai_media.followup",
            category: "ai_media",
            title: `${templateName} needs your input`,
            body: m.message || "Please answer a question in AI Chat to continue generation.",
            read: false,
            created_at: new Date().toISOString(),
            metadata: { session_id: sessionId, asset_type: assetType, template_name: templateName },
          };
          setItems((prev) => {
            if (prev.some((n) => n.id === syntheticId)) return prev;
            return [syntheticRow, ...prev];
          });
          setUnread((u) => u + 1);
          return;
        }

        // All other stages — update the live generation card
        if (sessionId) {
          setLiveGen({
            session_id: sessionId,
            asset_type: assetType,
            template_name: templateName,
            stage,
            message: m.message ?? stage,
            progress,
            elapsed_seconds: m.elapsed_seconds,
            remaining_seconds: m.remaining_seconds,
            estimated_time: m.estimated_time,
          });
        }
        return;
      }
      if (msg.type === "notification:dm_alert") {
        const title = String(msg.title ?? "New message");
        const body = String(msg.body ?? "");
        const messageId = String(msg.message_id ?? "");
        const threadId = String(msg.thread_id ?? "");
        pushToastRef.current(title, body, messageId ? `dm:${messageId}` : undefined);

        // Also add it synthetically to the bell dropdown so they don't lose it if they miss the toast
        const syntheticId = `dm:${messageId || Date.now()}`;
        const syntheticRow: NotificationRow = {
          id: syntheticId,
          type: "dm.message",
          category: "social",
          title: title,
          body: body,
          read: false,
          created_at: new Date().toISOString(),
          metadata: { thread_id: threadId, click_action: "OPEN_CHAT" },
        };
        setItems((prev) => {
          if (prev.some((n) => n.id === syntheticId)) return prev;
          return [syntheticRow, ...prev];
        });
        setUnread((u) => u + 1);
        return;
      }
      // job_event type is handled entirely by useJobToast — skip here
      if (msg.type === "notification:job_event") return;
      if (msg.type !== "notification:new_batch") return;
      const batch = (msg.notifications ?? []) as NotificationRow[];
      for (const row of batch) {
        const t = String(row.type ?? "");
        // Job notification types have styled toasts from useJobToast — skip generic toast
        if (SKIP_TOAST_TYPES.has(t) || JOB_TOAST_TYPES.has(t)) continue;
        if (!row.read) {
          pushToastRef.current(row.title || "Notification", row.body || "", row.id);
        }
      }
      fetchUnreadCountRef.current();
      if (openRef.current) fetchListRef.current();
      // Dispatch custom event so MyInventoryPage can auto-reload on deal analysis events
      const INVENTORY_RELOAD_TYPES = ["inventory.deal_ai_done", "inventory.deal_received", "inventory.deal_published"];
      const evtType = String((msg as Record<string, unknown>).event_type ?? "");
      const hasInventoryEvent = INVENTORY_RELOAD_TYPES.includes(evtType)
        || batch.some((r) => INVENTORY_RELOAD_TYPES.includes(String(r.type ?? "")));
      if (hasInventoryEvent) {
        window.dispatchEvent(new CustomEvent("inventory:deal_updated", { detail: { event_type: evtType || "inventory.deal_published" } }));
      }
    });
  }, [token]);

  const markRead = async (id: string) => {
    if (!token) return;
    try {
      const res = await fetch(`${NOTIFICATIONS_BASE}/${id}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setItems((prev) =>
          prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
        );
        setUnread((u) => Math.max(0, u - 1));
      }
    } catch {
      /* ignore */
    }
  };

  const markAllRead = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${NOTIFICATIONS_BASE}/read-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setItems((prev) => prev.map((n) => ({ ...n, read: true })));
        setUnread(0);
      }
    } catch {
      /* ignore */
    }
  };

  const openDeepLink = (n: NotificationRow) => {
    const path = getNotificationDeepLink(n);
    navigate(path);
    setOpen(false);
  };

  const formatTime = (iso: string) => formatISTChatListTime(iso) || "";

  if (!token) return null;

  const CATEGORY_ICONS: Record<string, string> = {
    system: "📢", security: "🔒", social: "👥", inventory: "📦",
    community: "💬", processing: "⚙️", enquiry: "📩", ai_media: "✨",
    deal: "🏷️", webinar: "🎙️", news: "📰",
  };
  const getCatIcon = (cat: string, type?: string) => {
    if (type?.startsWith("ai_media.")) return "✨";
    if (type?.startsWith("deal.feed.")) return "🔥";
    if (type?.startsWith("security.")) return "🔒";
    if (type?.startsWith("enquiry.")) return "📩";
    return CATEGORY_ICONS[cat] || "🔔";
  };

  const UNREAD_CAT_BG: Record<string, string> = {
    security: "bg-red-50", ai_media: "bg-indigo-50",
    enquiry: "bg-amber-50", deal: "bg-emerald-50",
  };

  return (
    <>
      {/* ── Fancy toast stack ─────────────────────────────────────────────── */}
      <div
        className="fixed left-1/2 top-16 z-[100] flex -translate-x-1/2 flex-col gap-2 pointer-events-none px-2"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const cfg =
            t.variant === "success" ? { bar: "bg-emerald-500", bg: "bg-white border-emerald-200", icon: "✅", title: "text-emerald-800" } :
            t.variant === "error"   ? { bar: "bg-red-500",     bg: "bg-white border-red-200",     icon: "❌", title: "text-red-800" } :
            t.variant === "warning" ? { bar: "bg-amber-500",   bg: "bg-white border-amber-200",   icon: "⚠️", title: "text-amber-800" } :
            t.variant === "info"    ? { bar: "bg-blue-500",    bg: "bg-white border-blue-200",    icon: "ℹ️", title: "text-blue-800" } :
                                      { bar: "bg-gray-400",    bg: "bg-white border-gray-200",    icon: "🔔", title: "text-gray-900" };
          return (
            <div
              key={t.id}
              className={`pointer-events-auto max-w-sm w-full rounded-xl border shadow-xl overflow-hidden ${cfg.bg}`}
              style={{ animation: "slide-in-from-top 0.25s ease-out" }}
            >
              <div className={`h-0.5 w-full ${cfg.bar}`} />
              <div className="px-4 py-3 flex items-start gap-2.5">
                <span className="text-base flex-shrink-0 mt-0.5" aria-hidden>{cfg.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-semibold leading-tight ${cfg.title}`}>{t.title}</p>
                  {t.body && <p className="mt-0.5 text-xs text-gray-500 line-clamp-2 leading-relaxed">{t.body}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Bell button + dropdown ─────────────────────────────────────────── */}
      <div className="relative flex shrink-0 items-center" style={{ zIndex: 99999 }}>
        <button
          ref={bellRef}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="btn-icon relative h-10 w-10 transition-transform active:scale-90"
          aria-expanded={open}
          aria-haspopup="true"
          aria-label="Notifications"
        >
          {/* Animated bell */}
          <span
            className={`text-xl inline-block transition-transform ${unread > 0 ? "animate-[ring_1.2s_ease-in-out_infinite]" : ""}`}
            style={{ transformOrigin: "50% 0%" }}
            aria-hidden
          >
            🔔
          </span>
          {/* Live generation pulse ring */}
          {liveGen && (
            <span className="absolute inset-0 rounded-full border-2 border-indigo-400 animate-ping opacity-60 pointer-events-none" />
          )}
          {/* Unread badge */}
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-rose-600 px-1 text-[10px] font-bold text-white shadow-sm shadow-red-500/30 ring-2 ring-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>

        {open && (
          <div
            ref={dropdownRef}
            className="absolute right-0 top-full mt-2 flex max-h-[min(32rem,calc(100vh-6rem))] w-[min(24rem,calc(100vw-1rem))] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
            style={{ zIndex: 99999, animation: "dropdown-in 0.18s ease-out" }}
            role="menu"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-2">
                <span className="text-base">🔔</span>
                <span className="text-sm font-bold text-gray-900">Notifications</span>
                {unread > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-600 border border-red-200">
                    {unread} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={() => void markAllRead()}
                    className="rounded-lg px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { navigate("/notifications"); setOpen(false); }}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                  title="Open notifications page"
                  aria-label="Open full notifications page"
                >
                  <svg viewBox="0 0 16 16" fill="none" className="w-4 h-4" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            {/* ── Live generation card ───────────────────────────────────── */}
            {liveGen && (
              <div className="border-b border-indigo-100 bg-gradient-to-br from-indigo-50 via-purple-50/60 to-white px-4 py-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
                    </span>
                    <span className="text-xs font-semibold text-indigo-700">
                      {liveGen.template_name || liveGen.asset_type} generating…
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400 font-mono tabular-nums">
                    {liveGen.progress}%{liveGen.elapsed_seconds != null ? ` · ${liveGen.elapsed_seconds}s` : ""}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-2 leading-snug">{liveGen.message}</p>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-indigo-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all duration-700"
                    style={{ width: `${liveGen.progress}%` }}
                  />
                </div>
                {(liveGen.remaining_seconds != null || liveGen.estimated_time) && (
                  <p className="mt-1 text-[10px] text-amber-600 font-medium">
                    {liveGen.remaining_seconds != null
                      ? liveGen.remaining_seconds > 60
                        ? `~${Math.ceil(liveGen.remaining_seconds / 60)}m ${liveGen.remaining_seconds % 60}s remaining`
                        : `~${liveGen.remaining_seconds}s remaining`
                      : liveGen.estimated_time}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => { navigate("/chat"); setOpen(false); }}
                  className="mt-2.5 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 py-1.5 text-center text-xs font-semibold text-white hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm shadow-indigo-500/20"
                >
                  Go to AI Chat ↗
                </button>
              </div>
            )}

            {/* ── Notification list ──────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto bg-white">
              {loadingList && items.length === 0 && (
                <div className="flex flex-col items-center gap-2 px-3 py-10 text-center">
                  <div className="w-6 h-6 rounded-full border-2 border-indigo-300 border-t-indigo-600 animate-spin" />
                  <span className="text-xs text-gray-400">Loading…</span>
                </div>
              )}
              {!loadingList && items.length === 0 && !liveGen && (
                <div className="flex flex-col items-center gap-3 px-3 py-12 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-2xl">🔕</div>
                  <div>
                    <div className="text-sm font-medium text-gray-700">All caught up!</div>
                    <div className="text-xs text-gray-400 mt-0.5">No new notifications</div>
                  </div>
                </div>
              )}
              <ul>
                {items.map((n, idx) => {
                  const catIcon = getCatIcon(n.category, n.type);
                  const unreadBg = !n.read ? (UNREAD_CAT_BG[n.category] || "bg-blue-50/60") : "";
                  const isLast = idx === items.length - 1;
                  return (
                    <li
                      key={n.id}
                      onClick={() => { if (!n.read) markRead(n.id); openDeepLink(n); }}
                      className={`group flex gap-3 px-3 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${unreadBg} ${!isLast ? "border-b border-gray-100/80" : ""}`}
                    >
                      {/* Category icon */}
                      <div className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-xl flex items-center justify-center text-base ${!n.read ? "bg-white shadow-sm border border-gray-100" : "bg-gray-100"}`}>
                        {catIcon}
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm leading-snug line-clamp-1 ${!n.read ? "font-semibold text-gray-900" : "font-medium text-gray-700"}`}>
                          {n.title}
                        </p>
                        <p className="mt-0.5 line-clamp-2 text-xs text-gray-500 leading-relaxed">
                          {n.body}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[10px] text-gray-400">{formatTime(n.created_at)}</span>
                          <span className="text-[10px] text-gray-300">·</span>
                          <span className="text-[10px] text-gray-400 capitalize">{n.category}</span>
                          {!n.read && (
                            <span className="ml-auto w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); openDeepLink(n); }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                          title="Open"
                        >
                          <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth="1.8">
                            <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                        {!n.read && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-gray-500 hover:bg-emerald-50 hover:text-emerald-600 transition-colors"
                            title="Mark read"
                          >
                            <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth="1.8">
                              <path d="M2.5 8.5l3.5 3.5 7-7" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-100 bg-white px-3 py-2.5">
              <Link
                to="/notifications"
                className="flex items-center justify-center gap-1.5 rounded-xl py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
                onClick={() => setOpen(false)}
              >
                View all notifications
                <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5" stroke="currentColor" strokeWidth="1.8">
                  <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </Link>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes ring {
          0%, 100% { transform: rotate(0deg); }
          10%, 30% { transform: rotate(-12deg); }
          20%, 40% { transform: rotate(12deg); }
          50% { transform: rotate(0deg); }
        }
        @keyframes slide-in-from-top {
          from { opacity: 0; transform: translateY(-8px) translateX(-50%); }
          to   { opacity: 1; transform: translateY(0)  translateX(-50%); }
        }
        @keyframes dropdown-in {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </>
  );
}
